import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid,
  List as ListIcon,
  Calendar as CalendarIcon,
  Plus,
  Sparkles,
  Loader2,
  Image as ImageIcon,
  Inbox,
  AlertTriangle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { isContentPolicyBlockError, isContentSafetyUnavailableError } from "@/lib/api";
import useBillingSubscription from "@/lib/useBillingSubscription";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ContentFilters from "@/components/ContentFilters";
import PostCard from "@/components/PostCard";
import PostEditorDialog from "@/components/PostEditorDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/Surface";

const STATUS_KEYS = ["all", "draft", "pending_approval", "scheduled", "paused", "published", "failed"];

// ---------------------------------------------------------------------------
// Load-failure UI
// ---------------------------------------------------------------------------
// These are rendered when GET /api/posts fails after one retry. We keep them
// visually distinct from the "no posts" empty state so users never see
// "0 posts" when the real issue is a transient backend / network hiccup.

function LoadErrorPanel({ status, onRetry }) {
  return (
    <div
      className="py-12 text-center"
      data-testid="posts-load-error"
      role="alert"
    >
      <div className="h-12 w-12 mx-auto rounded-full bg-[color:var(--surface-3)] flex items-center justify-center text-amber-500 mb-3">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="font-display text-lg font-semibold" data-testid="posts-load-error-title">
        We couldn&apos;t load your posts right now.
      </div>
      <div className="text-sm text-[color:var(--text-3)] mt-1">
        Your posts have not been deleted.
      </div>
      <div className="mt-4">
        <Button
          variant="outline"
          onClick={onRetry}
          data-testid="posts-load-error-retry"
        >
          Retry
        </Button>
      </div>
      {status ? (
        <div className="mt-2 text-[11px] text-[color:var(--text-4)]" data-testid="posts-load-error-status">
          status: {String(status)}
        </div>
      ) : null}
    </div>
  );
}

function StaleDataBanner({ status, onRetry }) {
  return (
    <div
      className="mb-3 rounded-md border border-amber-300/40 bg-amber-50/10 px-3 py-2 text-sm flex items-center gap-3"
      data-testid="posts-stale-banner"
      role="status"
    >
      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
      <div className="flex-1">
        Couldn&apos;t refresh your posts (status {String(status)}). Showing the last
        successful list &mdash; nothing has been deleted.
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onRetry}
        data-testid="posts-stale-banner-retry"
      >
        Retry
      </Button>
    </div>
  );
}

// --- Tiny inline visual calendar (read-only, click cell to open day's posts) ----
function MiniCalendarView({ posts, onOpenPost }) {
  const [monthDate, setMonthDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const cells = useMemo(() => {
    const start = monthDate;
    const startWeekday = start.getDay();
    const days = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < startWeekday; i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(new Date(start.getFullYear(), start.getMonth(), d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [monthDate]);
  const byDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const stamp = p.scheduled_at || p.published_at;
      if (!stamp) continue;
      const key = new Date(stamp).toISOString().slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts]);
  const monthLabel = monthDate.toLocaleString(undefined, { month: "long", year: "numeric" });
  return (
    <div data-testid="content-calendar-view">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg font-semibold">{monthLabel}</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} data-testid="content-cal-prev">←</Button>
          <Button size="sm" variant="ghost" onClick={() => setMonthDate(new Date())} data-testid="content-cal-today">Today</Button>
          <Button size="sm" variant="ghost" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} data-testid="content-cal-next">→</Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[110px]" />;
          const key = d.toISOString().slice(0, 10);
          const items = byDay[key] || [];
          return (
            <div key={key} className="min-h-[110px] rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-1.5">
              <div className="text-[11px] text-[color:var(--text-2)] mb-1">{d.getDate()}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((p) => (
                  <button
                    key={p.post_id}
                    type="button"
                    onClick={() => onOpenPost?.(p)}
                    className="block w-full text-left rounded-md overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] hover:border-[color:var(--border-strong)] transition-colors"
                    data-testid="content-cal-pill"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-12 object-cover" />
                    ) : (
                      <div className="w-full h-12 bg-gradient-to-br from-[color:var(--surface-3)] to-[color:var(--surface-2)] flex items-center justify-center text-[color:var(--text-3)]">
                        <ImageIcon className="h-3.5 w-3.5" />
                      </div>
                    )}
                    <div className="px-1 py-0.5 text-[10px] text-[color:var(--text-2)] truncate flex items-center gap-1">
                      <StatusBadge status={p.status} />
                    </div>
                  </button>
                ))}
                {items.length > 3 ? (
                  <div className="text-[10px] text-[color:var(--text-3)] text-center">+{items.length - 3} more</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ContentManagerPage() {
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({ status: "all", platform: "all", source: "all", q: "", sort: "newest" });
  const [view, setView] = useState("grid"); // grid | list | calendar
  const [openPost, setOpenPost] = useState(null);
  const [loading, setLoading] = useState(true);
  // ``loadError`` is set to the last failed refresh's HTTP status (or
  // ``"network"`` for a network-level failure). It is intentionally
  // SEPARATE from ``posts`` so we NEVER conflate "we couldn't reach the
  // server" with "you have zero posts". A failed refresh preserves the
  // previous ``posts`` value.
  const [loadError, setLoadError] = useState(null);
  // Pending delete state \u2014 polished ConfirmDialog instead of window.confirm.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Per-post in-flight publish tracker. Backing the Publish/Retry button
  // with immediate local state (before the network round-trip) is what
  // stops the double-click duplicate-publish reported by the Meta reviewer.
  // The backend also enforces a per-post lock (see /posts/{id}/publish) so
  // the guard is defense-in-depth, not the only line of defense.
  const [publishingIds, setPublishingIds] = useState(() => new Set());
  // Read-only subscription snapshot for the multi-platform publish
  // pre-check. Never mutates usage.
  const { sub: billingSub } = useBillingSubscription();

  const load = async (f = filters) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (f.status && f.status !== "all") params.set("status", f.status);
    if (f.platform && f.platform !== "all") params.set("platform", f.platform);
    if (f.source && f.source !== "all") params.set("source", f.source);
    if (f.q) params.set("q", f.q);
    if (f.sort) params.set("sort", f.sort);
    const url = "/posts?" + params.toString();

    // Retry ONCE for network errors and 500/502/503/504. Everything else
    // (401/403/etc) surfaces immediately.
    const TRANSIENT = new Set([500, 502, 503, 504]);
    const isTransient = (err) => {
      const s = err?.response?.status;
      if (s == null) return true; // network / abort / CORS
      return TRANSIENT.has(s);
    };

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await api.get(url);
        setPosts(res.data || []);
        setLoadError(null);
        setLoading(false);
        return;
      } catch (err) {
        const status = err?.response?.status;
        if (attempt === 0 && isTransient(err)) {
          // eslint-disable-next-line no-console
          console.warn(
            "[content-manager] /posts transient failure (status=",
            status ?? "network",
            ") — retrying once",
          );
          await new Promise((r) => setTimeout(r, 400));
          continue;
        }
        // Give up. Do NOT clear ``posts`` — the previously loaded list
        // stays visible. Show the error panel via ``loadError``.
        // eslint-disable-next-line no-console
        console.error(
          "[content-manager] /posts load failed",
          { status, message: err?.message || null },
        );
        setLoadError({ status: status || "network" });
        setLoading(false);
        return;
      }
    }
  };

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.platform, filters.source, filters.sort]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(filters), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  // counts per status from a separate unfiltered call
  const [counts, setCounts] = useState({});
  // Spec R3: distinguish "brand-new account, zero posts" from "filters or
  // search returned zero results". Both paths currently share the same
  // ``posts.length === 0`` render branch, so we compute the raw filter
  // signal here \u2014 any non-default filter or a non-empty search string
  // counts as "filters active".
  const hasActiveFilters = useMemo(
    () =>
      (filters.status && filters.status !== "all") ||
      (filters.platform && filters.platform !== "all") ||
      (filters.source && filters.source !== "all") ||
      !!(filters.q && filters.q.trim()),
    [filters.status, filters.platform, filters.source, filters.q],
  );
  const refreshCounts = async () => {
    try {
      const res = await api.get("/posts");
      const all = res.data || [];
      const c = { all: all.length };
      for (const k of STATUS_KEYS) {
        if (k === "all") continue;
        c[k] = all.filter((p) => p.status === k).length;
      }
      setCounts(c);
    } catch (_e) {
      /* ignore */
    }
  };
  useEffect(() => {
    refreshCounts();
  }, []);

  const handleAction = async (kind, post) => {
    try {
      if (kind === "delete") {
        // Defer to polished ConfirmDialog \u2014 actual delete is in
        // `confirmDelete()`. Doing it here keeps the action API consistent.
        setDeleteTarget(post);
        return;
      } else if (kind === "approve") {
        await api.post(`/posts/${post.post_id}/approve`);
        toast.success("Approved & scheduled");
      } else if (kind === "reject") {
        await api.post(`/posts/${post.post_id}/reject`);
        toast.success("Rejected");
      } else if (kind === "pause") {
        await api.post(`/posts/${post.post_id}/pause`);
        toast.success("Paused");
      } else if (kind === "resume") {
        await api.post(`/posts/${post.post_id}/resume`);
        toast.success("Resumed");
      } else if (kind === "publish") {
        // Multi-platform quota pre-check (frontend convenience only — the
        // backend still enforces via 402 when it computes remaining). This
        // catches the case where a user clicks Publish before PostCard's
        // own gate has settled.
        const targets = Array.isArray(post.target_platforms) && post.target_platforms.length > 0
          ? post.target_platforms
          : [post.platform];
        const unitsNeeded = Math.max(1, targets.length);
        const monthlyRemaining = billingSub?.monthly_remaining;
        if (monthlyRemaining != null && unitsNeeded > monthlyRemaining) {
          toast.error(
            `You have ${monthlyRemaining} post${monthlyRemaining === 1 ? "" : "s"} remaining this month. Publishing to ${targets.join(" + ")} would use ${unitsNeeded} posts. Remove one platform or upgrade to continue.`,
            { id: `publish-${post.post_id}`, duration: 8000 },
          );
          return;
        }
        // Duplicate-click guard: if the button is already publishing this
        // post, bail early. The PostCard button is disabled while
        // publishingIds contains post.post_id, but a rapid keyboard/enter
        // click can still slip through — this check makes the guard
        // authoritative.
        if (publishingIds.has(post.post_id)) return;
        setPublishingIds((prev) => {
          const next = new Set(prev);
          next.add(post.post_id);
          return next;
        });
        // Immediate feedback so the user doesn't wonder if the click worked.
        const publishToastId = toast.loading("Publishing started. This may take a few seconds…", {
          id: `publish-${post.post_id}`,
        });
        try {
          const res = await api.post(`/posts/${post.post_id}/publish`);
          const data = res?.data;
          if (data?.status === "blocked_safety") {
            // Content Safety Phase S1 — Gate B blocked this publish
            // attempt. The post remains preserved as a draft-like row
            // with status=blocked_safety so the user can edit + retry.
            toast.error(
              data.error_message ||
                "This content can't be published because it may violate Postmoro's content safety rules. Please revise and try again.",
              { id: publishToastId },
            );
          } else if (data?.status === "blocked_org_policy") {
            // Phase 20 — Gate C blocked this publish because the
            // business's own configured content restrictions were
            // violated. Surface the persisted violations so the user
            // knows which rule to revise. This is NOT a regulatory
            // block — it's the business's OWN opt-in policy.
            const violations = Array.isArray(data.org_compliance_violations)
              ? data.org_compliance_violations
              : [];
            const summary = violations.length
              ? violations
                  .slice(0, 3)
                  .map((v) => v.message || v.rule || "policy violation")
                  .join(" • ")
              : (data.error_message ||
                  "This post was held because it may not match your organization's content policy.");
            toast.error(summary, { id: publishToastId, duration: 10000 });
          } else if (data?.blocked_by === "org_compliance_unavailable") {
            // Fail-closed on semantic evaluator outage. Post is NOT
            // published — surface a retryable message.
            toast.error(
              data.error_message ||
                "We couldn't complete the required organization content policy check right now. Nothing was published. Please try again shortly.",
              { id: publishToastId },
            );
          } else if (data?.status === "failed") {
            toast.error(data.error_message || "Publish couldn't complete. Open the post to see details.", { id: publishToastId });
          } else if (data?.status === "partial_published") {
            toast.warning(data.error_message || "Partially published — some platforms failed.", { id: publishToastId });
          } else if (data?.status === "published") {
            toast.success("Published", { id: publishToastId });
          } else {
            // Unknown / non-terminal status — never claim a false success.
            toast.info(
              data?.error_message || "Publish is still processing. Open the post to see its status.",
              { id: publishToastId },
            );
          }
        } catch (err) {
          // Backend returns HTTP 409 with a structured detail when a
          // parallel publish request is already in flight — surface a
          // dedicated "still publishing" message instead of the generic
          // "Action failed" so double-clicks feel handled, not broken.
          const detail = err?.response?.data?.detail;
          const isInFlight = err?.response?.status === 409 || detail?.error === "publish_in_progress";
          if (isInFlight) {
            toast.info(detail?.message || "This post is already publishing. Please wait for it to finish.", { id: publishToastId });
          } else if (isContentPolicyBlockError(err) || isContentSafetyUnavailableError(err)) {
            // Interceptor already toasted a generic message; silence the
            // per-post loading toast (no duplicate).
            toast.dismiss(publishToastId);
          } else {
            toast.error(
              (typeof detail === "string" ? detail : detail?.message) ||
                "Publish failed",
              { id: publishToastId },
            );
          }
        } finally {
          setPublishingIds((prev) => {
            const next = new Set(prev);
            next.delete(post.post_id);
            return next;
          });
        }
      } else if (kind === "duplicate") {
        await api.post(`/posts/${post.post_id}/duplicate`);
        toast.success("Duplicated as draft");
      } else if (kind === "regenerate-caption") {
        toast.info("Regenerating caption…");
        await api.post(`/posts/${post.post_id}/regenerate-caption`);
        toast.success("Caption regenerated");
      } else if (kind === "regenerate-image") {
        toast.info("Regenerating image · ~20s");
        await api.post(`/posts/${post.post_id}/regenerate-image`);
        toast.success("Image regenerated");
      } else if (kind === "regenerate") {
        toast.info("Regenerating post · ~25s");
        await api.post(`/posts/${post.post_id}/regenerate`);
        toast.success("Post regenerated");
      }
      await load(filters);
      await refreshCounts();
    } catch (err) {
      if (isContentPolicyBlockError(err) || isContentSafetyUnavailableError(err)) {
        // Interceptor toasted generic message already.
        return;
      }
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const onChanged = async (updated, opts = {}) => {
    if (opts.added || updated?.post_id) {
      await load(filters);
      await refreshCounts();
    }
  };
  const onDeleted = async () => {
    await load(filters);
    await refreshCounts();
  };

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Content Manager"
          subtitle="The operational hub for every post you've drafted, scheduled, or published."
          testidPrefix="content"
          action={
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/autopilot">
                <Button variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="content-open-autopilot">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Autopilot
                </Button>
              </Link>
              <Link to="/generate">
                <Button className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" data-testid="content-new-post">
                  <Plus className="h-4 w-4 mr-1.5" /> New post
                </Button>
              </Link>
            </div>
          }
        />

        <GlassCard className="p-4 mb-4" delay={0.05}>
          <ContentFilters filters={filters} onChange={setFilters} counts={counts} />
        </GlassCard>

        <Tabs value={view} onValueChange={setView}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <TabsList className="bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]">
              <TabsTrigger value="grid" data-testid="view-grid"><LayoutGrid className="h-4 w-4 mr-1" /> Grid</TabsTrigger>
              <TabsTrigger value="list" data-testid="view-list"><ListIcon className="h-4 w-4 mr-1" /> List</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="view-calendar"><CalendarIcon className="h-4 w-4 mr-1" /> Calendar</TabsTrigger>
            </TabsList>
            <div className="text-xs text-[color:var(--text-3)] font-mono-tabular">
              {posts.length} post{posts.length === 1 ? "" : "s"}
            </div>
          </div>

          <TabsContent value="grid">
            <GlassCard className="p-4" delay={0.08}>
              {loading ? (
                <div className="py-16 text-center text-[color:var(--text-3)] inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading posts…
                </div>
              ) : loadError && posts.length === 0 ? (
                <LoadErrorPanel status={loadError.status} onRetry={() => load(filters)} />
              ) : posts.length === 0 ? (
                <EmptyState
                  hasNoPostsAtAll={counts.all === 0}
                  hasActiveFilters={hasActiveFilters}
                />
              ) : (
                <>
                  {loadError && <StaleDataBanner status={loadError.status} onRetry={() => load(filters)} />}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="content-grid">
                  {posts.map((p) => (
                    <motion.div key={p.post_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                      <PostCard
                        post={p}
                        layout="grid"
                        isPublishing={publishingIds.has(p.post_id)}
                        onOpen={(post) => setOpenPost(post)}
                        onAction={handleAction}
                      />
                    </motion.div>
                  ))}
                </div>
                </>
              )}
            </GlassCard>
          </TabsContent>

          <TabsContent value="list">
            <GlassCard className="p-4" delay={0.08}>
              {loading ? (
                <div className="py-16 text-center text-[color:var(--text-3)] inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading posts…
                </div>
              ) : loadError && posts.length === 0 ? (
                <LoadErrorPanel status={loadError.status} onRetry={() => load(filters)} />
              ) : posts.length === 0 ? (
                <EmptyState
                  hasNoPostsAtAll={counts.all === 0}
                  hasActiveFilters={hasActiveFilters}
                />
              ) : (
                <>
                  {loadError && <StaleDataBanner status={loadError.status} onRetry={() => load(filters)} />}
                <div className="space-y-2" data-testid="content-list">
                  {posts.map((p) => (
                    <PostCard
                      key={p.post_id}
                      post={p}
                      layout="list"
                      isPublishing={publishingIds.has(p.post_id)}
                      onOpen={(post) => setOpenPost(post)}
                      onAction={handleAction}
                    />
                  ))}
                </div>
                </>
              )}
            </GlassCard>
          </TabsContent>

          <TabsContent value="calendar">
            <GlassCard className="p-4" delay={0.08}>
              <MiniCalendarView posts={posts} onOpenPost={(p) => setOpenPost(p)} />
            </GlassCard>
          </TabsContent>
        </Tabs>
      </div>

      <PostEditorDialog
        open={!!openPost}
        post={openPost}
        onOpenChange={(o) => { if (!o) setOpenPost(null); }}
        onChanged={onChanged}
        onDeleted={onDeleted}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete post?"
        description="This will remove the post from your dashboard. This action cannot be undone."
        secondaryNote={
          deleteTarget?.status === "published"
            ? "This will not remove the post from the social platform. It will only remove it from Postmoro. The post will still count toward this month\u2019s published usage."
            : null
        }
        confirmLabel="Delete post"
        cancelLabel="Cancel"
        destructive
        loading={deleting}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleting(true);
          try {
            await api.delete(`/posts/${deleteTarget.post_id}`);
            toast.success("Post deleted");
            setPosts((cur) => cur.filter((p) => p.post_id !== deleteTarget.post_id));
            refreshCounts();
            setDeleteTarget(null);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete post");
          } finally {
            setDeleting(false);
          }
        }}
        testidPrefix="content-delete"
      />
    </div>
  );
}

function EmptyState({ hasNoPostsAtAll = false, hasActiveFilters = false }) {
  // Spec R3: brand-new account (never generated anything) gets a CTA
  // pointing straight to /generate, not a message about "clear the
  // filter" that would confuse them. Otherwise (they DO have posts but
  // the current view returned zero) keep the existing wording.
  if (hasNoPostsAtAll && !hasActiveFilters) {
    return (
      <div className="py-12 text-center" data-testid="content-empty-first-run">
        <div className="h-12 w-12 mx-auto rounded-full bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)] mb-3">
          <Inbox className="h-5 w-5" />
        </div>
        <div className="font-display text-lg font-semibold">
          You haven&apos;t created any posts yet
        </div>
        <div className="text-sm text-[color:var(--text-3)] mt-1 max-w-md mx-auto">
          Generate your first post to get started \u2014 Postmoro will draft the caption,
          hashtags, and image, and you approve before anything ships.
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Link to="/generate">
            <Button
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid="content-empty-generate-cta"
            >
              Generate your first post
            </Button>
          </Link>
          <Link to="/autopilot">
            <Button
              variant="outline"
              className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
              data-testid="content-empty-autopilot-cta"
            >
              Try Autopilot
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="py-12 text-center" data-testid="content-empty">
      <div className="h-12 w-12 mx-auto rounded-full bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)] mb-3">
        <Inbox className="h-5 w-5" />
      </div>
      <div className="font-display text-lg font-semibold">No posts match these filters</div>
      <div className="text-sm text-[color:var(--text-3)] mt-1">Try clearing the search or status filter. New posts? Use <Link to="/generate" className="text-[color:var(--brand-primary)] hover:underline">Generate</Link> or <Link to="/autopilot" className="text-[color:var(--brand-primary)] hover:underline">Autopilot</Link>.</div>
    </div>
  );
}
